import { GetString } from 'fluent-react';
import React, { RefObject } from 'react';
import {
  CountryProfile,
  GlobalDatum,
  Product,
  ProductClass,
  ProductSpaceDatum,
  ProductSpaceInput,
} from '../../graphQL/types/shared';
import { gql, useQuery } from '../../graphQL/useQuery';
import { SlideElementProps } from '../../presentationManager/PresentationManager';
import {
  IndicatorsContainerBase,
} from '../Grid';
import GlossaryTerm, { TooltipAlign, TooltipType } from '../landing/GlossaryTerm';
import Indicator, {
  Status,
  Type,
} from '../topPageIndicator/Indicator';

const query = gql`
  query ExportBasketTopOfPageIndicators($productClass: ProductClass!, $year: Int!, $location: ID!) {
    location: countryProfile(location: $location) {
      latestCoiRank
    }
    globalData: globalDatum {
      latestCoiRankTotal
    }
    productSpace(productClass: $productClass, year: $year, location: $location) {
      rca
      product {
        id
      }
    }
  }
`;

interface SuccessResponse {
  location: null | {
    latestCoiRank: CountryProfile['latestCoiRank'] | null,
  };
  globalData: {
    latestCoiRankTotal: GlobalDatum['latestCoiRankTotal'],
  };
  productSpace: Array<{
    rca: ProductSpaceDatum['rca'];
    product: {
      id: Product['id'];
    };
  }>;
}

interface Props extends SlideElementProps {
  countryId: string;
  year: number;
  getFluentString: GetString;
  zIndex: number;
}

const TopOfPageIndicators = React.forwardRef((props: Props, rootElRef: RefObject<HTMLDivElement>) => {
  const variables: ProductSpaceInput = {
    productClass: ProductClass.HS,
    year: props.year,
    location: props.countryId,
  };
  const {loading, error, data} = useQuery<SuccessResponse, ProductSpaceInput>(query, {variables});
  const {
    getFluentString, zIndex,
  } = props;
  const exportProductsLaebl = getFluentString('export-products-indicator-label');
  const coiRankLabel = (
    <GlossaryTerm
        type={TooltipType.Standard}
        term={getFluentString('complexity-outlook-index-indicator-label')}
        explanation={getFluentString('glossary-complexity-outlook-index-term-definition')}
        citation={undefined}
        align={TooltipAlign.Standard}
        attachment={null}
      />
  );
  const rcaGreaterThanOne = (
    <GlossaryTerm
      type={TooltipType.Standard}
      term={'(RCA>1)'}
      explanation={getFluentString('country-product-space-top-indicator-rca-grater-than-one')}
      citation={undefined}
      align={TooltipAlign.Standard}
      attachment={null}
    />

  );

  let out: React.ReactElement<any> | null;
  if (loading === true) {
    out = (
      <>
        <Indicator
          type={Type.Text}
          status={Status.Loading}
          label={exportProductsLaebl}
        />
        <Indicator
          type={Type.Rank}
          status={Status.Loading}
          label={coiRankLabel}
        />
      </>
    );
  } else if (error !== undefined) {
    console.error(error);
    out = (
      <>
        <Indicator
          type={Type.Rank}
          status={Status.Failure}
          label={exportProductsLaebl}
        />
        <Indicator
          type={Type.Rank}
          status={Status.Failure}
          label={coiRankLabel}
        />
      </>
    );
  } else if (data !== undefined) {
    const {location, globalData: {latestCoiRankTotal}, productSpace} = data;
    if (location === null) {
      out = (
        <>
          <Indicator
            type={Type.Rank}
            status={Status.Failure}
            label={exportProductsLaebl}
          />
          <Indicator
            type={Type.Rank}
            status={Status.Failure}
            label={coiRankLabel}
          />
        </>
      );
    } else {
      const { latestCoiRank } = location;
      const rcaCount = productSpace.filter(({rca}) => rca >= 1).length;

      const exportProductsIndicator = (
        <Indicator
          status={Status.Success}
          type={Type.Rank}
          label={exportProductsLaebl}
          numerator={rcaCount}
          suffix={''}
          denominator={rcaGreaterThanOne}
        />
      );
      const complexityOutlookIndexIndicator = (
        <Indicator
          type={Type.Rank}
          status={Status.Success}
          label={coiRankLabel}
          numerator={latestCoiRank}
          suffix={latestCoiRank !== null ? getFluentString('ordinal-rank-without-number', {rank: latestCoiRank}) : ''}
          denominator={getFluentString('coi-rank-denominator', {total: latestCoiRankTotal})}
        />
      );
      out = (
        <>
          {exportProductsIndicator}
          {complexityOutlookIndexIndicator}
        </>
      );
    }

  } else {
    out = null;
  }
  const style: React.CSSProperties = {
    zIndex,
  };
  return (
    <IndicatorsContainerBase
      style={style}
      ref={rootElRef}
    >
      {out}
    </IndicatorsContainerBase>
  );
});

export default TopOfPageIndicators;
